import { useEffect, useCallback } from 'react';
import { WizardProvider, useWizard } from './context/WizardContext';
import { ProgressBar } from './components/UI/ProgressBar';
import { WelcomeStep } from './components/Steps/WelcomeStep';
import { PurchaseStep } from './components/Steps/PurchaseStep';
import { ContactStep } from './components/Steps/ContactStep';
import { FiscalStep } from './components/Steps/FiscalStep';
import { AttachmentsStep } from './components/Steps/AttachmentsStep';
import { SuccessStep } from './components/Steps/SuccessStep';
import { Button } from './components/UI/Button';
import { Trash2 } from 'lucide-react';
import { STEPS } from './types';

function WizardApp() {
  const { state, nextStep, prevStep, reset, currentStepId, canGoNext } = useWizard();

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        prevStep();
      } else if (canGoNext && state.currentStep > 0) {
        nextStep();
      }
    }
  }, [nextStep, prevStep, canGoNext, state.currentStep]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const renderStep = () => {
    if (state.isComplete) {
      return <SuccessStep />;
    }

    switch (currentStepId) {
      case 'welcome':
        return <WelcomeStep />;
      case 'purchase':
        return <PurchaseStep />;
      case 'contact':
        return <ContactStep />;
      case 'fiscal':
        return <FiscalStep />;
      case 'attachments':
        return <AttachmentsStep />;
      default:
        return <WelcomeStep />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <ProgressBar />
      
      <div className="container mx-auto px-4 py-8">
        <header className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Portal de Facturación</h1>
              <p className="text-gray-600">Solicita tu factura en pocos pasos</p>
            </div>
            
            {state.currentStep > 0 && !state.isComplete && (
              <Button
                onClick={reset}
                variant="ghost"
                size="sm"
                icon={Trash2}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Borrar y empezar de nuevo
              </Button>
            )}
          </div>

          {state.currentStep > 0 && !state.isComplete && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Paso {state.currentStep} de {STEPS.length - 1}
                </span>
                <span className="text-sm text-gray-500">
                  {STEPS[state.currentStep]?.title}
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary-500 transition-all duration-500 ease-out"
                  style={{ width: `${((state.currentStep) / (STEPS.length - 1)) * 100}%` }}
                />
              </div>
            </div>
          )}
        </header>

        <main className="py-8">
          {renderStep()}
        </main>

        <footer className="mt-12 pt-8 border-t border-gray-200">
          <div className="text-center text-sm text-gray-500">
            <p>Portal de Facturación Cliente • Prototipo interactivo</p>
            <p className="mt-1">Usa <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Enter</kbd> para continuar, <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Shift+Enter</kbd> para regresar</p>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <WizardProvider>
      <WizardApp />
    </WizardProvider>
  );
}